import React, { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useNavigate } from 'react-router-dom'
import useStore from '../hooks/useStore'

const IconSun = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="8" cy="8" r="2.5"/>
    <path d="M8 1.5V3M8 13V14.5M1.5 8H3M13 8H14.5M3.4 3.4l1.1 1.1M11.5 3.4l-1.1 1.1M3.4 12.6l1.1-1.1M11.5 12.6l-1.1-1.1"/>
  </svg>
)

const IconBolt = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.5 1.5H5.5L3.5 8.5H7L5 14.5L13 7H9L9.5 1.5Z"/>
  </svg>
)

const IconTarget = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="8" cy="8" r="6.5"/>
    <circle cx="8" cy="8" r="3"/>
    <circle cx="8" cy="8" r="1.2" fill="currentColor" stroke="none"/>
  </svg>
)

const IconChart = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M2 13.5V9M5.5 13.5V5M9 13.5V7.5M12.5 13.5V3"/>
    <path d="M0.5 13.5H15.5"/>
  </svg>
)

const IconCalendar = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 2.5V4.5M13 2.5V4.5M2 6H14"/>
    <rect x="2" y="3.5" width="12" height="10" rx="2"/>
  </svg>
)

const IconCanvas = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1.2"/>
    <rect x="9" y="1.5" width="5.5" height="5.5" rx="1.2"/>
    <rect x="1.5" y="9" width="5.5" height="5.5" rx="1.2"/>
    <rect x="9" y="9" width="5.5" height="5.5" rx="1.2"/>
  </svg>
)

const IconGlobe = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="8" r="6.5"/>
    <path d="M1.5 8h13M8 1.5c1.7 1.8 2.6 4 2.6 6.5S9.7 12.7 8 14.5M8 1.5C6.3 3.3 5.4 5.5 5.4 8s.9 4.7 2.6 6.5"/>
  </svg>
)

const IconChevron = ({ collapsed }) => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    {collapsed
      ? <path d="M4 7h6M8 4.5l2.5 2.5-2.5 2.5"/>
      : <path d="M10 7H4M6 4.5L3.5 7 6 9.5"/>
    }
  </svg>
)

// Pin icon — locked = sidebar stays open, unlocked = auto-collapses
const IconPin = ({ pinned }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {pinned
      ? <><path d="M9 2l5 5-2 2-2-1-4 4v2H4v-2L1 9l1-1 4-4-1-2z"/><line x1="1" y1="15" x2="6" y2="10"/></>
      : <><path d="M9 2l5 5-2 2-2-1-4 4v2H4v-2L1 9l1-1 4-4-1-2z" strokeDasharray="2 2"/><line x1="1" y1="15" x2="6" y2="10" opacity="0.4"/></>
    }
  </svg>
)

// Portal tooltip — position is passed in directly from the mouse event (synchronous)
function NavTooltip({ tooltip }) {
  if (!tooltip) return null
  return createPortal(
    <div className="sb-nav-tooltip" style={{ top: tooltip.top, left: tooltip.left }}>
      {tooltip.label}
    </div>,
    document.body
  )
}

function Sidebar() {
  const navigate = useNavigate()
  const { history, selectedMonthNumber } = useStore()
  const [collapsed, setCollapsed] = useState(false)
  const [pinned, setPinned] = useState(false)
  const [tooltip, setTooltip] = useState(null)  // { label, top, left }

  const toggleCollapse = () => {
    const next = !collapsed
    setCollapsed(next)
    document.body.classList.toggle('sb-collapsed', next)
    if (!next) setTooltip(null)
  }

  const togglePin = () => setPinned(p => !p)

  const flowItems = [
    { path: '/monthly',  icon: <IconCalendar />, label: 'Monthly Results Area', badge: new Date(2026, selectedMonthNumber - 1, 1).toLocaleString('en-US', { month: 'short' }).toUpperCase() },
    { path: '/morning',  icon: <IconSun />,      label: 'Morning Setup',        badge: 'W' },
    { path: '/focus',    icon: <IconBolt />,     label: 'Focus Blocks',         badge: 'C+T' },
    { path: '/score',    icon: <IconTarget />,   label: 'End-of-Day Score',     badge: 'R' },
    { path: '/history',  icon: <IconChart />,    label: 'History',              badge: Array.isArray(history) ? history.length.toString() : '0' },
  ]

  const expertItems = [
    { path: '/canvas', icon: <IconCanvas />, label: 'Command Canvas', badge: 'ALL' },
  ]

  const handleNavClick = useCallback(() => {
    if (!pinned && !collapsed) {
      setCollapsed(true)
      document.body.classList.add('sb-collapsed')
    }
  }, [pinned, collapsed])

  const NavItem = ({ item }) => (
    <NavLink
      to={item.path}
      className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
      onClick={handleNavClick}
      onMouseEnter={(e) => {
        if (collapsed) {
          const rect = e.currentTarget.getBoundingClientRect()
          setTooltip({
            label: item.label,
            top: rect.top + rect.height / 2,
            left: rect.right + 10,
          })
        }
      }}
      onMouseLeave={() => setTooltip(null)}
    >
      <span className="nav-ico">{item.icon}</span>
      {!collapsed && <span className="nav-item-label">{item.label}</span>}
      {!collapsed && <span className="nav-bdg">{item.badge}</span>}
    </NavLink>
  )

  return (
    <>
      <NavTooltip tooltip={tooltip} />

      <nav className={`sidebar${collapsed ? ' sidebar-collapsed' : ''}`}>

        <div className="sb-top-row">
          <button className="sb-collapse-btn" onClick={toggleCollapse} title={collapsed ? 'Expand' : 'Collapse'}>
            <IconChevron collapsed={collapsed} />
          </button>
          {!collapsed && (
            <button
              className={`sb-pin-btn${pinned ? ' pinned' : ''}`}
              onClick={togglePin}
              title={pinned ? 'Auto-collapse on click (currently pinned open)' : 'Pin sidebar open'}
            >
              <IconPin pinned={pinned} />
            </button>
          )}
        </div>

        {!collapsed && <div className="nav-lbl">Flow</div>}
        {collapsed && <div className="nav-lbl-dot" />}

        {flowItems.map(item => <NavItem key={item.path} item={item} />)}

        {!collapsed && <div className="nav-lbl" style={{marginTop: 16}}>Expert</div>}
        {collapsed && <div className="nav-lbl-dot" style={{marginTop: 12}} />}

        {expertItems.map(item => <NavItem key={item.path} item={item} />)}

        {!collapsed && (
          <div className="sb-bottom">
            <div className="sb-eq">
              <span className="R">R</span>
              <span className="op"> = (</span>
              <span className="W">W</span>
              <span className="op"> × </span>
              <span className="C">C</span>
              <span className="op">) + </span>
              <span className="T">T</span>
            </div>
            <div className="sb-eq-meta">
              <span className="sb-eq-sub">Universal Equation</span>
              <span className="sb-version">v1.4.0</span>
            </div>
            <div className="sb-bottom-actions">
              <button type="button" className="sb-fund-link" title="Learn how W, C, T create results" onClick={() => navigate('/fundamentals')}>
                Fundamentals
              </button>
              <button type="button" className="sb-community-link" onClick={() => navigate('/community-live')} title="Community Live">
                Community
              </button>
            </div>
          </div>
        )}

        {collapsed && (
          <div className="sb-bottom sb-bottom-collapsed">
            <button
              type="button"
              className="sb-fund-link"
              style={{width:'28px',height:'28px',display:'flex',alignItems:'center',justifyContent:'center',borderRadius:'50%',padding:0,letterSpacing:0}}
              onClick={() => navigate('/fundamentals')}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                setTooltip({ label: 'Fundamentals', top: rect.top + rect.height / 2, left: rect.right + 10 })
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              ?
            </button>
            <button
              type="button"
              className="sb-fund-link"
              style={{width:'28px',height:'28px',display:'flex',alignItems:'center',justifyContent:'center',borderRadius:'50%',padding:0,marginTop:'4px',opacity:0.5}}
              onClick={() => navigate('/community-live')}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect()
                setTooltip({ label: 'Community', top: rect.top + rect.height / 2, left: rect.right + 10 })
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              <IconGlobe />
            </button>
            <div style={{fontSize:'8px',color:'var(--text3)',opacity:0.35,letterSpacing:'0.05em',fontFamily:'var(--ff-m)',textAlign:'center',marginTop:'2px'}}>1.4</div>
          </div>
        )}
      </nav>
    </>
  )
}

export default Sidebar
